/**
 * ForceUpdateOverlay
 * Full-screen blocking overlay for force-update messages.
 * Non-dismissable — user must update the app to continue.
 * Blocks Android back button.
 */

import React, { useEffect } from 'react';
import { Modal, TouchableOpacity, StyleSheet, View, BackHandler } from 'react-native';
import { Text } from '@gluestack-ui/themed';
import { FS_SM, FS_LG, FS_MD } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { resolveMessageTexts } from '../../utils/messageI18n';
import { openURL } from '../../utils/linking';
import type { InAppMessagePayload } from '../../generated/api/http-contracts';

interface Props {
  message: InAppMessagePayload;
}

export function ForceUpdateOverlay({ message }: Props) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const texts = resolveMessageTexts(message.i18nKey);

  // Block Android back button
  useEffect(() => {
    const handler = () => true; // returning true prevents default behavior
    const subscription = BackHandler.addEventListener('hardwareBackPress', handler);
    return () => subscription.remove();
  }, []);

  const handleUpdate = () => {
    if (message.actionPayload) {
      void openURL(message.actionPayload);
    }
  };

  return (
    <Modal
      visible
      transparent={false}
      animationType="fade"
    >
      <View
        testID="force-update-overlay"
        style={[styles.container, { backgroundColor: theme.background }]}
      >
        <View style={styles.content}>
          {texts.title !== '' && (
            <Text
              testID="force-update-title"
              allowFontScaling={false}
              style={[styles.title, { fontSize: FS_LG * fontScale, color: theme.text, ...ffs }]}
            >
              {texts.title}
            </Text>
          )}

          {texts.body !== '' && (
            <Text
              testID="force-update-body"
              allowFontScaling={false}
              style={[styles.body, { fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }]}
            >
              {texts.body}
            </Text>
          )}

          {message.actionPayload && texts.action !== '' && (
            <TouchableOpacity
              testID="force-update-action"
              accessibilityLabel={texts.action}
              accessibilityRole="button"
              onPress={handleUpdate}
              style={[styles.button, { backgroundColor: theme.primary }]}
            >
              <Text
                allowFontScaling={false}
                style={[styles.buttonText, { fontSize: FS_MD * fontScale, color: '#FFFFFF', ...ffs }]}
              >
                {texts.action}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  content: {
    alignItems: 'center',
    maxWidth: 320,
  },
  title: {
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  body: {
    lineHeight: 22,
    marginBottom: 32,
    textAlign: 'center',
  },
  button: {
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 12,
    minWidth: 200,
    alignItems: 'center',
  },
  buttonText: {
    fontWeight: '600',
  },
});
